export class LegacyLoginControlPanel {
  readonly element: HTMLDialogElement;
  private readonly form: HTMLFormElement;
  private readonly username: HTMLInputElement;
  private readonly email: HTMLInputElement;
  private readonly message: HTMLTextAreaElement;
  private readonly submit: HTMLButtonElement;
  private readonly status: HTMLElement;
  private busy = false;

  constructor() {
    this.element = document.createElement('dialog');
    this.element.className = 'login-control-panel';
    this.element.setAttribute('aria-labelledby', 'login-control-panel-title');
    this.element.innerHTML = `
      <header class="login-control-panel__titlebar">
        <h2 id="login-control-panel-title">Contact support</h2>
        <button type="button" aria-label="Close">×</button>
      </header>
      <form class="login-control-panel__form">
        <label for="support-username">Username</label>
        <input id="support-username" name="username" autocomplete="username" maxlength="64" required />
        <label for="support-email">Email (Remember to check the spam folder for the answer, this is a small unknown mail server so it's easily filtered away.)</label>
        <input id="support-email" name="email" type="email" autocomplete="email" maxlength="254" required />
        <label for="support-message">Message</label>
        <textarea id="support-message" name="message" maxlength="10000" rows="7" required></textarea>
        <p class="login-control-panel__status" role="status" aria-live="polite"></p>
        <div class="login-control-panel__actions">
          <button type="button" data-action="cancel">Cancel</button>
          <button type="submit">Send</button>
        </div>
      </form>
    `;
    this.form = requiredElement(this.element, '.login-control-panel__form', HTMLFormElement);
    this.username = requiredElement(this.element, '[name="username"]', HTMLInputElement);
    this.email = requiredElement(this.element, '[name="email"]', HTMLInputElement);
    this.message = requiredElement(this.element, '[name="message"]', HTMLTextAreaElement);
    this.submit = requiredElement(this.element, '[type="submit"]', HTMLButtonElement);
    this.status = requiredElement(this.element, '.login-control-panel__status', HTMLElement);
    requiredElement(
      this.element,
      '.login-control-panel__titlebar button',
      HTMLButtonElement,
    ).addEventListener('click', () => this.close());
    requiredElement(this.element, '[data-action="cancel"]', HTMLButtonElement).addEventListener(
      'click',
      () => this.close(),
    );
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.send();
    });
  }

  open(username = ''): void {
    if (this.username.value.trim() === '') this.username.value = username;
    this.setStatus('');
    if (!this.element.open) this.element.showModal();
    (this.username.value.trim() === '' ? this.username : this.email).focus();
  }

  close(): void {
    if (this.element.open && !this.busy) this.element.close();
  }

  private async send(): Promise<void> {
    if (this.busy || !this.form.reportValidity()) return;
    this.setBusy(true);
    this.setStatus('Sending…');
    try {
      const response = await fetch('/api/v2/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: this.username.value.trim(),
          email: this.email.value.trim(),
          message: this.message.value.trim(),
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string | { message?: string };
      };
      if (!response.ok) {
        const detail = typeof payload.error === 'string' ? payload.error : payload.error?.message;
        throw new Error(detail ?? 'Your message could not be sent. Please try again.');
      }
      this.message.value = '';
      this.setStatus('Your message was sent. Support will reply by email.');
    } catch (error) {
      this.setStatus(
        error instanceof Error ? error.message : 'Your message could not be sent.',
        true,
      );
    } finally {
      this.setBusy(false);
    }
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.submit.disabled = busy;
    this.username.disabled = busy;
    this.email.disabled = busy;
    this.message.disabled = busy;
  }

  private setStatus(message: string, error = false): void {
    this.status.textContent = message;
    this.status.dataset.error = String(error);
  }
}

function requiredElement<T extends Element>(
  root: ParentNode,
  selector: string,
  constructor: new () => T,
): T {
  const element = root.querySelector(selector);
  if (!(element instanceof constructor)) throw new Error(`Missing UI element: ${selector}`);
  return element;
}
